<template>
  <div
    class="bet-item"
    :class="['bet-' + bet.result]"
    @click="$emit('click', bet)"
  >
    <div class="bet-item__header">
      <!-- Left side -->
      <div class="bet-item__left">
        <div class="bet-item__date">
          {{ formatDate(bet.created_at) }}
        </div>

        <div class="bet-item__meta">
          <span class="bet-item__code">{{ getBetCode }}</span>
          <span class="bet-item__type">{{ $t(bet.type) }}</span>
          <span v-if="bet.bet_context === 'live'" class="bet-item__live-badge">
            {{ $t('live') }}
          </span>
          <span v-if="bet.betFreeze" class="bet-item__freeze-badge" :class="{ 'bet-item__freeze-badge--overridden': bet.betFreezeOverridden }" :title="bet.betFreezeOverridden ? $t('betFreeze.overridden') : $t('betFreeze.badge')">&#10052;</span>
        </div>
      </div>

      <!-- Right side -->
      <div class="bet-item__actions">
        <BetStatusIcon :status="bet.result" />
        <svg class="bet-item__arrow" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
        </svg>
      </div>
    </div>

    <div class="bet-item__code-row">
      <span class="bet-item__label">{{ $t('bets.code') }}:</span>
      <span class="bet-item__value">{{ bet.code }}</span>
    </div>

    <div class="bet-item__amounts">
      <div>
        <span class="bet-item__label">{{ $t('amount') }}: </span>
        <span class="bet-item__value">{{ bet.currencySymbol }} {{ formatAmount(bet.amount_real) }}</span>
      </div>
      <div>
        <span class="bet-item__label">{{ $t('winning') }}: </span>
        <span class="bet-item__winning" :class="{ 'bet-item__winning--won': bet.winning_real }">
          <s v-if="originalWinningDisplay" class="bet-item__winning-original">{{ originalWinningDisplay }}</s>
          {{ getWinningDisplay }}
        </span>
      </div>
    </div>

    <slot name="cashout"></slot>
  </div>
</template>

<script>
import BetStatusIcon from './BetStatusIcon.vue';
import { pendingPotential, originalPotential } from '@/utils/betFreezeDisplay';

export default {
  name: 'BetItem',
  components: { BetStatusIcon },
  props: {
    bet: {
      type: Object,
      required: true
    }
  },
  computed: {
    getBetCode() {
      return this.bet.betCode || this.bet.code || '';
    },
    getWinningDisplay() {
      if (this.bet.winning_real) {
        return `${this.bet.currencySymbol} ${this.formatAmount(this.bet.winning_real)}`;
      }
      return this.getWinning(this.bet);
    },
    // Placement-time potential of a pending betFreeze bet, struck through.
    originalWinningDisplay() {
      if (this.bet.winning_real) return null;
      const original = originalPotential(this.bet);
      return original === null ? null : `${this.bet.currencySymbol || ''} ${this.formatAmount(original)}`;
    }
  },
  methods: {
    formatDate(date) {
      return new Date(date).toLocaleString();
    },
    formatAmount(amount) {
      return parseFloat(String(amount)).toFixed(2);
    },
    getWinning(bet) {
      const currencySymbol = bet.currencySymbol || '';
      switch (bet.type) {
        case 'single':
        case 'multiple': {
          // After a betFreeze the placement-time figure is obsolete.
          const potential = pendingPotential(bet);
          return `${currencySymbol} ${this.formatAmount(Number.isFinite(potential) ? potential : 0)}`;
        }
        case 'integral':
          if (bet._integral_detail && bet._integral_detail.winning_total_max !== undefined) {
            return `${currencySymbol} ${this.formatAmount(bet._integral_detail.winning_total_max)}`;
          }
          return `${currencySymbol} 0.00`;
        case 'system':
          if (bet._system_detail && bet._system_detail.winning_all_total !== undefined) {
            return `${currencySymbol} ${this.formatAmount(bet._system_detail.winning_all_total)}`;
          }
          return `${currencySymbol} 0.00`;
        default:
          return `${currencySymbol} 0.00`;
      }
    }
  }
};
</script>

<style scoped>
.bet-item {
  background-color: var(--mybets-card-bg);
  border-radius: .375rem;
  padding: 12px;
  border: 1px solid var(--mybets-border);
  cursor: pointer;
  transition: border-color 0.15s ease;
}

.bet-item:hover {
  border-color: var(--mybets-primary);
}

.bet-item__header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
}

.bet-item__left {
  min-width: 0;
}

.bet-item__date {
  font-size: var(--font-size-2xl);
  color: var(--mybets-text-muted);
  display: flex;
  align-items: center;
}

.bet-item__meta {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 4px;
}

.bet-item__code {
  font-size: var(--font-size-6xl);
  font-weight: 700;
  color: var(--mybets-primary);
}

.bet-item__type {
  font-size: var(--font-size-4xl);
  font-weight: 600;
  color: var(--mybets-text);
  text-transform: capitalize;
}

.bet-item__live-badge {
  padding: 2px 6px;
  font-size: var(--font-size-2xl);
  font-weight: 700;
  background-color: #dc2626;
  color: #ffffff;
  border-radius: 4px;
}

.bet-item__actions {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-left: 8px;
  justify-content: flex-end;
}

.bet-item__arrow {
  width: 20px;
  height: 20px;
  color: var(--mybets-text-muted);
}

.bet-item__code-row {
  margin-bottom: 4px;
}

.bet-item__label {
  font-size: var(--font-size-2xl);
  color: var(--mybets-text-muted);
}

.bet-item__value {
  font-size: var(--font-size-4xl);
  font-weight: 400;
  color: var(--mybets-text);
}

.bet-item__amounts {
  display: flex;
  align-items: center;
  gap: 16px;
  font-size: var(--font-size-4xl);
  justify-content: space-between;
}

.bet-item__winning {
  font-weight: 500;
  color: var(--mybets-primary);
}

.bet-item__winning--won {
  color: var(--mybets-winning-color);
}

/* betFreeze */
.bet-item__freeze-badge {
  color: #0ea5e9;
  font-size: var(--font-size-4xl);
  line-height: 1;
}

.bet-item__winning-original {
  opacity: 0.55;
  font-weight: 400;
  font-size: 0.85em;
  margin-right: 4px;
}
.bet-item__freeze-badge--overridden { opacity: 0.45; filter: grayscale(1); }
</style>
